import { IComponentOverlayParam, IOverlayDelete, IResult } from "@/types/map";
import { loadModules } from "esri-loader";
import { Vue } from "vue-property-decorator";
import CustomOverlay from "./CustomOverlay.vue";

interface IVueComponentInfo {
  type: string;
  id: string;
  component: Vue;
  position: __esri.Point;
  anchor: string;
  xoffset: number;
  yoffset: number;
}

export class ComponentOverlay {
  private static intances: Map<string, ComponentOverlay>;

  public static getInstance(view: __esri.MapView | __esri.SceneView) {
    const id = view.container.id;
    if (!ComponentOverlay.intances) {
      ComponentOverlay.intances = new Map();
    }
    let intance = ComponentOverlay.intances.get(id);
    if (!intance) {
      intance = new ComponentOverlay(view);
      ComponentOverlay.intances.set(id, intance);
    }
    return intance;
  }

  private view!: __esri.MapView | __esri.SceneView;
  private Point!: __esri.PointConstructor;
  private initialized = false;
  private watchHandle: __esri.WatchHandle | undefined;

  private vueComponentInfos: IVueComponentInfo[] = [];

  private constructor(view: __esri.MapView | __esri.SceneView) {
    this.view = view;
  }

  private async initialize(): Promise<void> {
    type MapModules = [typeof import("esri/geometry/Point")];
    const [Point] = await (loadModules([
      "esri/geometry/Point",
    ]) as Promise<MapModules>);
    this.Point = Point;
    this.initialized = true;
  }

  public async addComponentOverlay(
    params: IComponentOverlayParam
  ): Promise<IResult> {
    if (!this.initialized) {
      await this.initialize();
    }

    let addCount = 0;
    for (const overlay of params.overlays) {
      const vueComponent = overlay.vue || params.vue;
      if (!vueComponent) continue;

      const anchor = overlay.anchor || params.anchor || "center-center";
      const type = overlay.type || params.type || "";
      const id = overlay.id || "";
      // 锚点之外的额外偏移量
      const userXoffset = overlay.xoffset || 0;
      const userYoffset = overlay.yoffset || 0;

      const component: Vue = new Vue({
        render: (h) => h(vueComponent, { props: overlay.props }),
      }).$mount();
      this.view.container.appendChild(component.$el);

      const position = new this.Point({
        x: overlay.x,
        y: overlay.y,
        spatialReference: this.view.spatialReference.isWebMercator
          ? { wkid: 4326 }
          : this.view.spatialReference,
      });
      const vueComponentInfo: IVueComponentInfo = {
        component,
        position,
        anchor,
        type,
        id,
        xoffset: userXoffset,
        yoffset: userYoffset,
      };
      this.vueComponentInfos.push(vueComponentInfo);

      this.setVuePosition(vueComponentInfo);

      addCount++;
    }

    if (!this.watchHandle && addCount > 0) {
      this.watchHandle = this.view.watch("extent", (extent: any) => {
        this.vueComponentInfos.forEach((vueComponentInfo) => {
          this.setVuePosition(vueComponentInfo);
          // 根据zoom范围隐藏或显示
          if (
            params.zoomRange &&
            params.zoomRange instanceof Array &&
            params.zoomRange.length > 1
          ) {
            if (
              this.view.zoom < params.zoomRange[0] ||
              this.view.zoom > params.zoomRange[1]
            ) {
              this.setDisplay(vueComponentInfo, false);
            } else {
              this.setDisplay(vueComponentInfo, true);
            }
          }
        });
      });
    }

    return {
      status: 0,
      message: "ok",
      result: `成功添加${params.overlays.length}中的${addCount}个覆盖物`,
    };
  }

  //添加定制覆盖物
  public async addCustomComponentOverlay(
    params: IComponentOverlayParam
  ): Promise<IResult> {
    for (const overlay of params.overlays) {
      overlay.vue = CustomOverlay;
    }
    const res = this.addComponentOverlay(params);
    return res;
  }

  // 设置组件显示或隐藏
  private setDisplay(
    vueComponentInfo: IVueComponentInfo,
    display: boolean
  ): void {
    (vueComponentInfo.component.$el as any).style.visibility = display
      ? "visible"
      : "hidden";
  }

  /**
   * 根据锚点位置和额外偏移量，确定组件的left/top
   * @param vueComponentInfo
   */
  private setVuePosition(vueComponentInfo: IVueComponentInfo): void {
    const width = vueComponentInfo.component.$el.clientWidth;
    const height = vueComponentInfo.component.$el.clientHeight;
    let anchorXoffset = 0;
    let anchorYoffset = 0;

    switch (vueComponentInfo.anchor) {
      case "top-left":
        anchorXoffset = width / 2;
        anchorYoffset = height / 2;
        break;
      case "top-center":
        anchorXoffset = 0;
        anchorYoffset = height / 2;
        break;
      case "top-right":
        anchorXoffset = -width / 2;
        anchorYoffset = height / 2;
        break;
      case "center-left":
        anchorXoffset = width / 2;
        anchorYoffset = 0;
        break;
      case "center-center":
        anchorXoffset = 0;
        anchorYoffset = 0;
        break;
      case "center-right":
        anchorXoffset = -width / 2;
        anchorYoffset = 0;
        break;
      case "bottom-left":
        anchorXoffset = width / 2;
        anchorYoffset = -height / 2;
        break;
      case "bottom-center":
        anchorXoffset = 0;
        anchorYoffset = -height / 2;
        break;
      case "bottom-right":
        anchorXoffset = -width / 2;
        anchorYoffset = -height / 2;
        break;
    }

    const screenPoint = this.view.toScreen(vueComponentInfo.position);
    (vueComponentInfo.component.$el as any).style.left =
      screenPoint.x + anchorXoffset + vueComponentInfo.xoffset + "px";
    (vueComponentInfo.component.$el as any).style.top =
      screenPoint.y + anchorYoffset + vueComponentInfo.yoffset + "px";
  }

  public deleteComponentOverlay(params?: IOverlayDelete): IResult {
    let deleteCount = 0;
    for (var i = 0; i < this.vueComponentInfos.length; i++) {
      const vueComponentInfo = this.vueComponentInfos[i];
      if (
        !params ||
        (params.types && params.types.includes(vueComponentInfo.type)) ||
        (params.ids && params.ids.includes(vueComponentInfo.id))
      ) {
        const element = vueComponentInfo.component.$el;
        if (element.parentElement) {
          element.parentElement.removeChild(element);
          i--;
          deleteCount++;
        }
      }
    }
    // 若清除全部vue，删除监听器。
    if (this.watchHandle && this.vueComponentInfos.length === 0) {
      this.watchHandle.remove();
      this.watchHandle = undefined;
    }
    return {
      status: 0,
      message: "ok",
      result: `成功删除${deleteCount}个覆盖物`,
    };
  }

  public clearAll(): IResult {
    let deleteCount = 0;
    const remove_index = new Set<number>(); // 记录移出的下标
    for (var i = 0; i < this.vueComponentInfos.length; i++) {
      const vueComponentInfo = this.vueComponentInfos[i];
      const element = vueComponentInfo.component.$el;
      if (element.parentElement) {
        remove_index.add(i);
        element.parentElement.removeChild(element);
        i--;
        deleteCount++;
      }
      vueComponentInfo.component.$destroy();
    }
    // 从vueComponentInfos中删除
    for (let i = this.vueComponentInfos.length - 1; i >= 0; --i) {
      if (remove_index.has(i)) {
        this.vueComponentInfos.splice(i, 1);
      }
    }
    // 若清除全部vue，删除监听器。
    if (this.watchHandle && this.vueComponentInfos.length === 0) {
      this.watchHandle.remove();
      this.watchHandle = undefined;
    }
    return {
      status: 0,
      message: "ok",
      result: `成功删除${deleteCount}个覆盖物`,
    };
  }
}
